import React, { useState, useEffect } from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GripVertical, X } from 'lucide-react';
import { FIXED_GROUP_COLUMNS, MAX_GROUP_COLUMNS, OPTIONAL_STANDARD_GROUP_COLUMNS } from '../config/groupColumns';
import { usePreferences } from '../../../shared/context/preferencesContext';

const GroupColumnConfigModal = ({ isOpen, onClose, teamId, groupKey, availableCustomProps = [] }) => {
  const { updateGroupsTableColumns, resetGroupsTableColumns, getGroupsTableColumns, loading } = usePreferences();
  const [enabledColumns, setEnabledColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const current = getGroupsTableColumns(groupKey);
      const fixedKeys = Object.keys(FIXED_GROUP_COLUMNS);
      const enabledSet = new Set([...fixedKeys, ...(current.enabled || [])]);
      const enabled = [...enabledSet];
      let order = [...(current.order || [])].filter((k) => enabledSet.has(k));
      fixedKeys.forEach((f) => {
        if (!order.includes(f)) order.unshift(f);
      });
      enabled.forEach((k) => {
        if (!order.includes(k)) order.push(k);
      });
      setEnabledColumns(enabled);
      setColumnOrder(order);
      setHasChanges(false);
    }
  }, [isOpen, groupKey, getGroupsTableColumns]);

  const fixedKeys = Object.keys(FIXED_GROUP_COLUMNS);
  const optionalStandardKeys = Object.keys(OPTIONAL_STANDARD_GROUP_COLUMNS);
  const customPropKeysForPicker = availableCustomProps.filter(
    (k) => !FIXED_GROUP_COLUMNS[k] && !OPTIONAL_STANDARD_GROUP_COLUMNS[k]
  );

  const getColumnLabel = (key) => {
    if (FIXED_GROUP_COLUMNS[key]) return FIXED_GROUP_COLUMNS[key].label;
    if (OPTIONAL_STANDARD_GROUP_COLUMNS[key]) return OPTIONAL_STANDARD_GROUP_COLUMNS[key].label;
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const isFixed = (key) => !!FIXED_GROUP_COLUMNS[key];

  const maxNonFixed = MAX_GROUP_COLUMNS - fixedKeys.length;

  const handleToggleColumn = (columnKey) => {
    if (isFixed(columnKey)) return;
    const newEnabled = enabledColumns.includes(columnKey)
      ? enabledColumns.filter((k) => k !== columnKey)
      : [...enabledColumns, columnKey];

    const nonFixedEnabled = newEnabled.filter((k) => !isFixed(k));
    if (nonFixedEnabled.length > maxNonFixed && !enabledColumns.includes(columnKey)) return;

    setEnabledColumns(newEnabled);
    const newOrder = columnOrder.filter((k) => newEnabled.includes(k));
    if (!enabledColumns.includes(columnKey)) newOrder.push(columnKey);
    setColumnOrder(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await updateGroupsTableColumns(groupKey, enabledColumns, columnOrder);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error('Error saving group column preferences:', err);
      alert('Failed to save column preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await resetGroupsTableColumns(groupKey);
      const current = getGroupsTableColumns(groupKey);
      setEnabledColumns([...(current.enabled || [])]);
      setColumnOrder([...(current.order || [])]);
      setHasChanges(false);
    } catch (err) {
      console.error('Error resetting column preferences:', err);
      alert('Failed to reset column preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const current = getGroupsTableColumns(groupKey);
    setEnabledColumns([...(current.enabled || [])]);
    setColumnOrder([...(current.order || [])]);
    setHasChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  const orderedEnabledColumns = columnOrder.filter((k) => enabledColumns.includes(k));
  const nonFixedEnabled = enabledColumns.filter((k) => !isFixed(k));

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={handleCancel} />
        <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-[#DDE2EE] rounded-xl">
          <div className="flex-1 overflow-y-auto bg-gray-50/30 p-6 relative">
            <button
              onClick={handleCancel}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 text-[#475569] transition-all rounded-lg z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[#0f172a] mb-1" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Available Columns
                </h3>
                <p className="text-xs text-[#475569] mb-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Group ID is always shown. Up to {maxNonFixed} more column{maxNonFixed !== 1 ? 's' : ''} (standard
                  or custom properties).
                </p>
                <div className="space-y-2 max-h-[450px] overflow-y-auto pr-2">
                  {/* Fixed columns */}
                  <div className="mb-3">
                    <div className="text-xs font-medium text-[#475569] mb-2 uppercase tracking-wider">Fixed</div>
                    {fixedKeys.map(key => (
                      <label
                        key={key}
                        className="flex items-center px-3 py-2 rounded-lg border mb-1"
                        style={{ backgroundColor: 'rgba(75, 87, 170, 0.2)', borderColor: '#4b57aa', cursor: 'default' }}
                      >
                        <input type="checkbox" checked readOnly disabled className="mr-3 rounded border-gray-300" />
                        <span className="text-sm text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                          {getColumnLabel(key)} <span className="text-xs opacity-70">(required)</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {/* Optional standard columns */}
                  <div className="mb-3">
                    <div className="text-xs font-medium text-[#475569] mb-2 uppercase tracking-wider">Standard Attributes</div>
                    {optionalStandardKeys.map((key) => {
                      const isEnabled = enabledColumns.includes(key);
                      const isDisabled = !isEnabled && nonFixedEnabled.length >= maxNonFixed;
                      return (
                        <label
                          key={key}
                          className={`flex items-center px-3 py-2 rounded-lg cursor-pointer transition-colors mb-1 ${
                            isEnabled ? 'border' : 'bg-white border border-gray-200 hover:bg-gray-50'
                          } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={isEnabled ? { backgroundColor: 'rgba(75, 87, 170, 0.2)', borderColor: '#4b57aa' } : {}}
                        >
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handleToggleColumn(key)}
                            disabled={isDisabled}
                            className="mr-3 rounded border-gray-300"
                          />
                          <span
                            className="text-sm"
                            style={{ fontFamily: 'Raleway, sans-serif', color: isEnabled ? '#0f172a' : '#475569' }}
                          >
                            {getColumnLabel(key)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {/* Custom property columns */}
                  {customPropKeysForPicker.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-[#475569] mb-2 uppercase tracking-wider">Custom Properties</div>
                      {customPropKeysForPicker.map((key) => {
                        const isEnabled = enabledColumns.includes(key);
                        const isDisabled = !isEnabled && nonFixedEnabled.length >= maxNonFixed;
                        return (
                          <label
                            key={key}
                            className={`flex items-center px-3 py-2 rounded-lg cursor-pointer transition-colors mb-1 ${
                              isEnabled ? 'border' : 'bg-white border border-gray-200 hover:bg-gray-50'
                            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            style={isEnabled ? { backgroundColor: 'rgba(75, 87, 170, 0.2)', borderColor: '#4b57aa' } : {}}
                          >
                            <input
                              type="checkbox"
                              checked={isEnabled}
                              onChange={() => handleToggleColumn(key)}
                              disabled={isDisabled}
                              className="mr-3 rounded border-gray-300"
                            />
                            <span className="text-sm" style={{ fontFamily: 'Raleway, sans-serif', color: isEnabled ? '#0f172a' : '#475569' }}>
                              {getColumnLabel(key)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {customPropKeysForPicker.length === 0 && (
                    <div className="text-sm text-[#475569] py-2 text-center" style={{ fontFamily: 'Raleway, sans-serif' }}>
                      No custom properties defined yet. Set group properties via the SDK to add custom columns.
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0f172a] mb-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Selected Columns (drag to reorder, max {MAX_GROUP_COLUMNS})
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {orderedEnabledColumns.length === 0 ? (
                    <div className="text-sm text-[#475569] text-center py-8" style={{ fontFamily: 'Raleway, sans-serif' }}>No columns selected</div>
                  ) : orderedEnabledColumns.map((key, index) => (
                    <DraggableColumnItem
                      key={key}
                      columnKey={key}
                      index={index}
                      label={getColumnLabel(key)}
                      isRequired={isFixed(key)}
                      onMove={(fromIndex, toIndex) => {
                        const newOrder = [...columnOrder];
                        const [removed] = newOrder.splice(fromIndex, 1);
                        newOrder.splice(toIndex, 0, removed);
                        setColumnOrder(newOrder);
                        setHasChanges(true);
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#DDE2EE] bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                disabled={saving || loading}
                className="px-4 py-2 text-sm font-medium text-[#475569] bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                Reset to Default
              </button>
              {hasChanges && <span className="text-xs text-amber-600" style={{ fontFamily: 'Raleway, sans-serif' }}>Unsaved changes</span>}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving || loading || !hasChanges}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 shadow-md"
                style={{ fontFamily: 'Raleway, sans-serif', backgroundColor: (!saving && !loading && hasChanges) ? '#4b57aa' : '#475569' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

const DraggableColumnItem = ({ columnKey, index, label, isRequired, onMove }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'group-column',
    item: () => ({ columnKey, index }),
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });
  const [{ isOver }, drop] = useDrop({
    accept: 'group-column',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        onMove(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    collect: monitor => ({ isOver: monitor.isOver() })
  });
  return (
    <div
      ref={node => drag(drop(node))}
      className={`flex items-center px-3 py-2 rounded-lg border cursor-move transition-all ${isDragging ? 'opacity-50' : isOver ? 'border-[#4b57aa]' : 'hover:bg-gray-50'}`}
      style={{ backgroundColor: isOver ? '#4b57aa' : '#ffffff', borderColor: isOver ? '#4b57aa' : '#e5e7eb', color: isOver ? '#ffffff' : '#0f172a' }}
    >
      <GripVertical className="h-4 w-4 mr-2" style={{ color: isOver ? '#ffffff' : '#475569' }} />
      <span className="flex-1 text-sm" style={{ fontFamily: 'Raleway, sans-serif', color: isOver ? '#ffffff' : '#0f172a' }}>
        {label}
        {isRequired && <span className="ml-2 text-xs opacity-70">(required)</span>}
      </span>
    </div>
  );
};

export default GroupColumnConfigModal;
